import { LinkExternal01, Lock04 } from '@timelyapp/icons/solid-icons';
import { Box, Button, CustomIcon, HoverOver, List, Skeleton, Text } from '@timelyapp/react';
import PrivacyIndicator from 'components/PrivacyIndicator';
import React, { lazy, Suspense, useCallback, useState } from 'react';

const ToolTimelineExample = lazy(
  () => import('@timelyapp/integration-cloud-sdk/components/data/tool/ToolTimelineExample')
);

type Props = {
  onOpenChange: (isOpen: boolean) => void;
};
export const TicMemoryTimelineHoverOverInfo = ({ onOpenChange }: Props) => {
  const [isOpen, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      onOpenChange(isOpen);
      setOpen(isOpen);
    },
    [onOpenChange]
  );

  return (
    <HoverOver
      onOpenChange={handleOpenChange}
      open={isOpen}
      openDelay={150}
      trigger={
        <div style={{ cursor: 'help' }} onClick={() => setOpen(true)}>
          <CustomIcon.Info />
        </div>
      }
    >
      <Box.Row gap={4 * 4} padding="medium" alignItems="flex-start" style={{ maxWidth: 740 }}>
        {isOpen && <TimeTrackingHelpText.AutomaticTimeTracking />}

        <Box.Column gap="medium" padding="large">
          <TimeTrackingHelpText.Privacy />
          <TimeTrackingHelpText.PrivacyPromises />
        </Box.Column>
      </Box.Row>
    </HoverOver>
  );
};

TicMemoryTimelineHoverOverInfo.displayName = 'TicMemoryTimelineHoverOverInfo';

/**
 * Probably want to organize this differently, but for quick compositions...
 */

const HelpText_AutomaticTimeTracking = () => {
  return (
    <Box.Column gap="medium" padding="large" maxWidth={450} width="50%">
      <Text.Headline size="small">Automatic time tracking</Text.Headline>
      <Suspense fallback={<Skeleton.Box height={300} borderRadius="large" />}>
        <ToolTimelineExample timelineHeight={300} generateMaxMemories={20} memories={[]} />
      </Suspense>
      <PrivacyIndicator />
      <Text>Your recorded work activity is shown here and can be used to log time entries.</Text>
      <Button asChild>
        <a
          href="https://support.timely.com/en/articles/9917926-new-timeline-memory-101"
          rel="noopener"
          target="_blank"
        >
          <LinkExternal01 />
          Learn more
        </a>
      </Button>
    </Box.Column>
  );
};

const HelpText_Privacy = () => {
  return (
    <Box.Column gap="medium" padding="large">
      <Text.Headline size="xsmall">Can my boss or colleague see my data?</Text.Headline>
      <Text.Paragraph size="small">No!</Text.Paragraph>
      <Text.Paragraph size="small">
        Your activities are always private – even after you submit your timesheet. Only you can see
        them.
      </Text.Paragraph>
      <Button asChild>
        <a
          href="https://support.timely.com/en/articles/1540850-can-my-boss-or-colleague-see-my-data"
          rel="noopener"
          target="_blank"
        >
          <LinkExternal01 />
          Learn more
        </a>
      </Button>
    </Box.Column>
  );
};

const HelpText_Privacy_Promises = () => {
  return (
    <Box.Column gap="medium" padding="large">
      <Text.Headline size="xsmall">Privacy promises</Text.Headline>
      <List gap="medium">
        <List.Item gap="medium" interactive={false} alignItems="flex-start">
          <List.Item.Icon>
            <Lock04 />
          </List.Item.Icon>
          Your memories are exclusively available to you. Nobody else can view your memories in
          Timely.
        </List.Item>
        <List.Item gap="medium" interactive={false} alignItems="flex-start">
          <List.Item.Icon>
            <Lock04 />
          </List.Item.Icon>
          All your information remains completely private and is never shared with anyone.
        </List.Item>
        <List.Item gap="medium" interactive={false} alignItems="flex-start">
          <List.Item.Icon>
            <Lock04 />
          </List.Item.Icon>
          While your Week View is visible to colleagues via Company View, your memories stay
          strictly private.
        </List.Item>
        <List.Item gap="medium" interactive={false} alignItems="flex-start">
          <List.Item.Icon>
            <Lock04 />
          </List.Item.Icon>
          We never sell or forward your information to any third parties.
        </List.Item>
      </List>
    </Box.Column>
  );
};

export const TimeTrackingHelpText = Object.assign(
  {},
  {
    AutomaticTimeTracking: HelpText_AutomaticTimeTracking,
    Privacy: HelpText_Privacy,
    PrivacyPromises: HelpText_Privacy_Promises,
  }
);
